import React from "react"
import PropTypes from "prop-types"
import SanityImage from "gatsby-plugin-sanity-image"
import useBreakpoints from "hooks/useBreakpoints"
import VideoHero from "components/VideoHero"
import theme from "styles/theme"

export const heroHeights = {
  xl: [764, 590, 446],
  lg: [560, 477, 301],
  md: [410, 330, 220],
}

const Hero = ({ title, background, size, video }) => {
  if (["xl", "lg", "md"].includes(size)) {
    return video
      ? <VideoHero title={title} background={background} size={size} video={video} />
      : <ImageHero title={title} background={background} size={size} />
  }
  return <TextHero title={title} />
}

const containerStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 24px",
  textAlign: "center",
}

export default Hero

const ImageHero = ({ background, size, title }) => {
  const { tablet, mobile } = useBreakpoints()
  const [dH, tH, mH] = heroHeights[size]

  const minWidth = mobile ? 375 : tablet ? 767 : 1178
  const minHeight = mobile ? mH : tablet ? tH : dH

  return (
    <div
      css={[
        containerStyles,
        {
          position: "relative",
          minHeight,
          backgroundColor:
            background?.asset?.metadata?.palette?.dominant?.background ||
            theme.gray,
          ":after": {
            content: "''",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 3,
            backgroundColor: "rgba(86, 90, 92, 0.12)",
          },
        },
        size === "xl" && {
          height: minHeight,
          minHeight: "calc(100vh - 350px)",
          maxHeight: "120vh",
        },
      ]}
    >
      {background && (
        <>
          <SanityImage
            {...background}
            width={16}
            height={2}
            css={{
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 1,
              pointerEvents: "none",
              objectFit: "cover",
              top: 0,
              left: 0,
            }}
            alt=""
          />
          <SanityImage
            {...background}
            width={minWidth}
            height={minHeight}
            css={{
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              objectFit: "cover",
              top: 0,
              left: 0,
            }}
            alt=""
          />
        </>
      )}
      <h1
        css={{
          ...theme.t1,
          position: "relative",
          margin: "0 auto",
          maxWidth: 942,
          color: theme.white,
          zIndex: 4,
        }}
      >
        {title}
      </h1>
    </div>
  )
}

const TextHero = ({ title }) => (
  <div
    css={{
      ...containerStyles,
      background: theme.lightGray,
      color: theme.darkGray,
      minHeight: 285,
      [theme.tablet]: { minHeight: 235 },
      [theme.mobile]: { minHeight: 217 },
    }}
  >
    <h1 css={{ ...theme.t2, maxWidth: 942 }}>{title}</h1>
  </div>
)

ImageHero.propTypes = {
  title: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["xl", "lg", "md"]).isRequired,
  background: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }).isRequired,
}

TextHero.propTypes = {
  title: PropTypes.string.isRequired,
}

Hero.propTypes = {
  title: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["xl", "lg", "md", "sm"]).isRequired,
  background: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }),
  video: PropTypes.shape({
    _key: PropTypes.string.isRequired,
    _type: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    poster: PropTypes.shape({
      asset: PropTypes.shape({
        metadata: PropTypes.shape({
          dimensions: PropTypes.shape({
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
    }),
  }),
}
